import moment from "moment";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";

import { toast } from "react-toastify";
import Loader from "components/V2/Loader/Loader";
import { CompanyPlan, IHireCompany } from "types/HireCompany";
import HireCompanyApi from "network/hire_company/hire_company";
import { useForm } from "react-hook-form";
import Button from "components/Button/ButtonV2";
import { FormCheckboxSelect } from "components/V2/Form/Checkbox/FormCheckboxSelect";
import HireCompanyUsageConstraintsApi from "network/hire_company/hire_company_usage_constraints";
import IconV2 from "components/V2/Icons/IconV2";

const enableText = (b: boolean) => b ? "Enabled" : "Disabled"
const numberValue = (n: number) => ({ value: n, label: n.toString() });
const enabledValue = (b: boolean) => ({ value: b, label: enableText(b) });
const enableOptions = () => [enabledValue(true), enabledValue(false)]

const companyToForm = (c: IHireCompany) => {
  const { resume_enabled, talent_search_enabled, video_recording_enabled, plan, ats_enabled,
    completed_monthly_job_interviews_limit, accounts_limit, active_jobs_limit } = c.UsageConstraints;
  return {
    plan,
    active_jobs_limit: numberValue(active_jobs_limit),
    accounts_limit: numberValue(accounts_limit),
    completed_monthly_job_interviews_limit: numberValue(completed_monthly_job_interviews_limit),
    resume_enabled: enabledValue(resume_enabled),
    ats_enabled: enabledValue(ats_enabled),
    talent_search_enabled: enabledValue(talent_search_enabled),
    video_recording_enabled: enabledValue(video_recording_enabled)
  }
}

const HireCompanyConstraints = () => {
  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue
  } = useForm({
    defaultValues: {
      plan: "Free plan" as CompanyPlan,
      active_jobs_limit: numberValue(5),
      completed_monthly_job_interviews_limit: numberValue(20),
      accounts_limit: numberValue(3),
      ats_enabled: enabledValue(false),
      resume_enabled: enabledValue(false),
      talent_search_enabled: enabledValue(false),
      video_recording_enabled: enabledValue(false),
    },
  });
  const planValue = watch("plan");
  const setPlan = (p: CompanyPlan) => setValue("plan", p);
  const { id } = useParams();
  const [company, setCompany] = useState<IHireCompany>();
  const [updating, setUpdating] = useState(false);
  const resetF = () => company && reset(companyToForm(company));;

  const update = async (data: ReturnType<typeof companyToForm>) => {
    setUpdating(true);
    try {
      if (!company) return;
      const constraints = await HireCompanyUsageConstraintsApi.updateCompanyUsageConstraint({
        id: company?.id,
        active_jobs_limit: data.active_jobs_limit.value,
        completed_monthly_job_interviews_limit: data.completed_monthly_job_interviews_limit.value,
        accounts_limit: data.accounts_limit.value,
        resume_enabled: data.resume_enabled.value,
        ats_enabled: data.ats_enabled.value,
        talent_search_enabled: data.talent_search_enabled.value,
        video_recording_enabled: data.video_recording_enabled.value,
        plan: data.plan,
      });
      if (!constraints) throw new Error(constraints);
      company.UsageConstraints = constraints;
      setCompany({ ...company });
      toast.success("Updated");
    } catch (e) {
      console.log(e);
      toast.error("Error while updating company constraints");
    }
    setUpdating(false);
  }

  // Fetch company data
  const fetchCompany = async () => {
    const c = await HireCompanyApi.getOne({ id });
    if (!c) {
      toast.error("Error while retrieving company");
      return;
    }
    setCompany(c);
  };
  useEffect(() => { if (id) fetchCompany() }, [id]);

  useEffect(() => {
    if (!company) return;
    resetF();
  }, [company])

  if (!company) return <Loader />;

  const { Admin, createdAt, UsageConstraints } = company;


  return (
    <form onSubmit={handleSubmit(update)} className="w-full h-full flex flex-col overflow-y-auto p-9 " style={{
      backgroundImage: 'linear-gradient(to bottom, #2F3454 40%, #F9F9F9 40%)',
    }}>


      <div className="text-white mb-9">
        <p className="text-4xl font-poppins font-bold mb-4">{company.name}</p>
        <div className="bg-white bg-opacity-10 rounded-lg p-6 w-[950px]">
          <Tag header="Head Recruiter" content={Admin.User.full_name} />
          <Tag header="Date of Login" content={moment(createdAt).format("D MMMM YYYY")} />
        </div>
      </div>

      <div className="bg-white rounded-3xl p-9">


        <div className="flex gap-9 mb-9 mx-auto w-fit">
          <Plan plan="Free Plan" value={planValue} onClick={setPlan} />
          <Plan plan="Paid Trial" value={planValue} onClick={setPlan} />
          <Plan plan="Starter" value={planValue} onClick={setPlan} />
          <Plan plan="Growth" value={planValue} onClick={setPlan} />
          <Plan plan="Enterprise" value={planValue} onClick={setPlan} />
        </div>

        <div className="grid grid-cols-2 gap-y-9 gap-x-[130px]">
          <Card current={UsageConstraints.completed_monthly_job_interviews_limit} title="Number of Monthly Interviews" >
            <FormCheckboxSelect
              control={control}
              name="completed_monthly_job_interviews_limit"
              select={{
                className: "flex-col gap-2",
                boxes: [
                  numberValue(10),
                  numberValue(20),
                  numberValue(200),
                  numberValue(500),
                ]
              }}

            />

          </Card>
          <Card current={UsageConstraints.accounts_limit} title="Number of Recruiter Seats" >
            <FormCheckboxSelect
              control={control}
              name="accounts_limit"
              select={{
                className: "flex-col gap-2",
                boxes: [
                  numberValue(1),
                  numberValue(3),
                  numberValue(5),
                  numberValue(10),
                ]
              }}

            />
          </Card>
          <Card current={enableText(UsageConstraints.talent_search_enabled)} title="Talent Search" >
            <FormCheckboxSelect
              control={control}
              name='talent_search_enabled'
              select={{
                className: "flex-col gap-2",
                boxes: enableOptions()
              }}

            />
          </Card>

          <Card current={enableText(UsageConstraints.ats_enabled)} title="Ats Enabled" >
            <FormCheckboxSelect
              control={control}
              name='ats_enabled'
              select={{
                className: "flex-col gap-2",
                boxes: enableOptions()
              }}

            />
          </Card>
          <Card current={enableText(UsageConstraints.video_recording_enabled)} title="Video Recordings" >
            <FormCheckboxSelect
              control={control}
              name='video_recording_enabled'
              select={{
                className: "flex-col gap-2",
                boxes: enableOptions()
              }}

            />
          </Card>
          <Card current={UsageConstraints.active_jobs_limit} title="Active Jobs" >
            <FormCheckboxSelect
              control={control}
              name="active_jobs_limit"
              select={{
                className: "flex-col gap-2",
                boxes: [
                  numberValue(2),
                  numberValue(5),
                  numberValue(20),
                  numberValue(50),
                  numberValue(200),
                ]
              }}

            />
          </Card>
          <Card current={enableText(UsageConstraints.resume_enabled)} title="Resume Review" >
            <FormCheckboxSelect
              control={control}
              name='resume_enabled'
              select={{
                className: "flex-col gap-2",
                boxes: enableOptions()
              }}

            />
          </Card>

        </div>
      </div>

      <div className=" ml-auto flex gap-9 mt-5 ">
        <Button disabled={updating} onClick={resetF} type="button" variant="secondary">Cancel</Button>
        <Button type="submit" loading={updating} variant="primary">Save chages</Button>
      </div>



    </form >
  );
};

const Plan = ({ plan, value, onClick }: { plan: CompanyPlan, value: CompanyPlan, onClick: (p: CompanyPlan) => void }) => {
  const current = plan === value;
  return <button onClick={() => onClick(plan)} type="button"
    className={`flex items-center gap-2  rounded-3xl p-4 font-poppins text-base font-bold hover:bg-odf hover:text-white
  ${current ? "bg-odf" : "bg-[#F9F9F9]"}
  ${current ? "text-white" : "text-[#8A8A8A]"} `}>
    {current && <IconV2 iconType="CHECK-CIRCLE" iconClassName={"fill-white"} />} {plan}
  </button>
}

const Tag = ({ header, content }) => <p className="font-poppins text-base ">
  <span className="font-semibold">• {header}:</span> {content}
</p>


const Card = ({ title, children, current }) => <div className="flex flex-col gap-9 w-[500px] rounded-xl border-[1px] border-light-gray p-6">
  <div className="flex justify-between">
    <p className="font-poppins text-xl font-bold">{title}</p>
    {children}

  </div>
  <p className="mt-auto font-poppins text-base font-semibold  border-t-[1px] border-[odf] text-[#8A8A8A] pt-4 ">
    Current Selection: {current}
  </p>
</div>

export default HireCompanyConstraints;
